import { type PointerEvent, type ReactNode, useRef, useState } from 'react';
import { Icon } from './Icon.js';

/**
 * A window inside the page that does not block it (07/10/2026): dragged by
 * its title bar, reduced to the bar, closed. The page under it keeps working.
 */
export function FloatingPanel({
  title,
  onClose,
  extra,
  children,
}: {
  title: ReactNode;
  onClose: () => void;
  /** More buttons on the bar, before reduce and close. */
  extra?: ReactNode;
  children: ReactNode;
}) {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [reduced, setReduced] = useState(false);
  const panel = useRef<HTMLElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  const start = (event: PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button') || !panel.current) return;
    const box = panel.current.getBoundingClientRect();
    drag.current = { dx: event.clientX - box.left, dy: event.clientY - box.top };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current || !panel.current) return;
    const box = panel.current.getBoundingClientRect();
    // The panel stays across the screen, the bar at least in view to drag it back.
    setPosition({
      x: Math.min(Math.max(event.clientX - drag.current.dx, 0), window.innerWidth - box.width),
      y: Math.min(Math.max(event.clientY - drag.current.dy, 0), window.innerHeight - 40),
    });
  };
  const stop = () => {
    drag.current = null;
  };

  return (
    <section
      ref={panel}
      className={reduced ? 'floating-panel reduced' : 'floating-panel'}
      style={
        position
          ? {
              left: position.x,
              top: position.y,
              right: 'auto',
              maxHeight: `calc(100dvh - ${position.y}px - 1rem)`,
            }
          : undefined
      }
      role="dialog"
      aria-modal="false"
    >
      <div
        className="floating-bar"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerCancel={stop}
      >
        <strong className="floating-title">{title}</strong>
        {extra}
        <button
          type="button"
          className="icon-button"
          title={reduced ? 'Espandi' : 'Riduci'}
          aria-label={reduced ? 'Espandi' : 'Riduci'}
          onClick={() => setReduced(!reduced)}
        >
          <Icon name={reduced ? 'down' : 'up'} />
        </button>
        <button
          type="button"
          className="icon-button"
          title="Chiudi"
          aria-label="Chiudi"
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
      </div>
      {!reduced && <div className="floating-body">{children}</div>}
    </section>
  );
}
